import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';

const runtimeCallbacks = {
  refresh: null,
  refreshChromeOnly: null,
  requestAppRefresh: null,
  requestPlanRender: null,
  syncPlanViewState: null,
  drawPlanInteractionOverlay: null,
  clearGlobalSelection: null,
  clearMultiSelection: null,
  resetTransientInteractionState: null,
  toggleLevelsTool: null,
  closeLevelsPanelForToolChange: null,
  current3DModalScenePreset: null,
  set3DModalScenePreset: null,
  normalize3DModalFilters: null,
  requestCurrent3DFrame: null,
  render3D: null,
  update3DLighting: null,
  after3DSceneRendered: null,
  after3DArticleSelected: null,
  activeDrawingSheetPositionPrefix: null,
  reloadLocalizedChrome: null
};
const planRefreshedListeners = new Set();

function configure(callbacks = {}) {
  Object.keys(runtimeCallbacks).forEach(key => {
    if (typeof callbacks[key] === 'function') runtimeCallbacks[key] = callbacks[key];
  });
}

function refresh(fit = false) {
  if (isPreparingProjectMutation()) return;
  if (typeof runtimeCallbacks.refresh === 'function') runtimeCallbacks.refresh(Boolean(fit));
}

function refreshChromeOnly(reason = '') {
  if (isPreparingProjectMutation()) return;
  if (typeof runtimeCallbacks.refreshChromeOnly === 'function') {
    runtimeCallbacks.refreshChromeOnly(reason);
  } else {
    refresh(false);
  }
}

function requestAppRefresh(fit = false, reason = '') {
  if (isPreparingProjectMutation()) return;
  if (typeof runtimeCallbacks.requestAppRefresh === 'function') {
    runtimeCallbacks.requestAppRefresh(Boolean(fit), reason);
  } else {
    refresh(Boolean(fit));
  }
}

function requestPlanRender(reason = '', options = {}) {
  if (isPreparingProjectMutation()) return;
  if (typeof runtimeCallbacks.requestPlanRender === 'function') {
    runtimeCallbacks.requestPlanRender(reason, options);
  } else {
    refresh(false);
  }
}

function syncPlanViewState() {
  if (typeof runtimeCallbacks.syncPlanViewState === 'function') runtimeCallbacks.syncPlanViewState();
}

function drawPlanInteractionOverlay() {
  if (typeof runtimeCallbacks.drawPlanInteractionOverlay === 'function') {
    runtimeCallbacks.drawPlanInteractionOverlay();
  }
}

function clearGlobalSelection() {
  return typeof runtimeCallbacks.clearGlobalSelection === 'function'
    ? runtimeCallbacks.clearGlobalSelection()
    : false;
}

function clearMultiSelection() {
  return typeof runtimeCallbacks.clearMultiSelection === 'function'
    ? runtimeCallbacks.clearMultiSelection()
    : false;
}

function resetTransientInteractionState() {
  return typeof runtimeCallbacks.resetTransientInteractionState === 'function'
    ? runtimeCallbacks.resetTransientInteractionState()
    : false;
}

function toggleLevelsTool() {
  return typeof runtimeCallbacks.toggleLevelsTool === 'function'
    ? runtimeCallbacks.toggleLevelsTool()
    : null;
}

function closeLevelsPanelForToolChange(options = {}) {
  return typeof runtimeCallbacks.closeLevelsPanelForToolChange === 'function'
    ? runtimeCallbacks.closeLevelsPanelForToolChange(options)
    : null;
}

function current3DModalScenePreset(view3d = null) {
  return typeof runtimeCallbacks.current3DModalScenePreset === 'function'
    ? runtimeCallbacks.current3DModalScenePreset(view3d)
    : null;
}

function set3DModalScenePreset(presetId) {
  return typeof runtimeCallbacks.set3DModalScenePreset === 'function'
    ? runtimeCallbacks.set3DModalScenePreset(presetId)
    : false;
}

function normalize3DModalFilters(view3d = null) {
  return typeof runtimeCallbacks.normalize3DModalFilters === 'function'
    ? runtimeCallbacks.normalize3DModalFilters(view3d)
    : null;
}

function requestCurrent3DFrame(options = {}) {
  if (typeof runtimeCallbacks.requestCurrent3DFrame === 'function') {
    return runtimeCallbacks.requestCurrent3DFrame(options);
  }
  return null;
}

function render3D(options = {}) {
  if (typeof runtimeCallbacks.render3D === 'function') {
    return runtimeCallbacks.render3D(options);
  }
  return null;
}

function update3DLighting(options = {}) {
  if (typeof runtimeCallbacks.update3DLighting === 'function') {
    return runtimeCallbacks.update3DLighting(options);
  }
  return null;
}

function after3DSceneRendered(context = {}) {
  if (typeof runtimeCallbacks.after3DSceneRendered === 'function') {
    runtimeCallbacks.after3DSceneRendered(context);
  }
}

function after3DArticleSelected(selection = null) {
  if (typeof runtimeCallbacks.after3DArticleSelected === 'function') {
    runtimeCallbacks.after3DArticleSelected(selection);
  }
}

function activeDrawingSheetPositionPrefix(systemId, memberId) {
  return typeof runtimeCallbacks.activeDrawingSheetPositionPrefix === 'function'
    ? runtimeCallbacks.activeDrawingSheetPositionPrefix(systemId, memberId)
    : null;
}

// Re-fetches the header company/category/system labels and the drawing-area
// toolbar labels for the now-active language and re-renders both — called
// after a language switch. Configured by app_bootstrap.js, which owns both
// fetch+apply functions; kept here (rather than importing app_bootstrap.js
// directly from the language switcher) to avoid a circular import, same
// reasoning as every other callback slot in this module.
async function reloadLocalizedChrome() {
  if (typeof runtimeCallbacks.reloadLocalizedChrome === 'function') {
    await runtimeCallbacks.reloadLocalizedChrome();
  }
}

function onPlanRefreshed(listener) {
  if (typeof listener !== 'function') return () => undefined;
  planRefreshedListeners.add(listener);
  return () => {
    planRefreshedListeners.delete(listener);
  };
}

function notifyPlanRefreshed(context = {}) {
  for (const listener of [...planRefreshedListeners]) {
    try {
      listener(context);
    } catch (error) {
      console.error('[KS] Plan refresh listener failed', error);
    }
  }
}

const AppRuntime = Object.freeze({
  configure,
  refresh,
  refreshChromeOnly,
  requestAppRefresh,
  requestPlanRender,
  syncPlanViewState,
  drawPlanInteractionOverlay,
  clearGlobalSelection,
  clearMultiSelection,
  resetTransientInteractionState,
  toggleLevelsTool,
  closeLevelsPanelForToolChange,
  current3DModalScenePreset,
  set3DModalScenePreset,
  normalize3DModalFilters,
  requestCurrent3DFrame,
  render3D,
  update3DLighting,
  after3DSceneRendered,
  after3DArticleSelected,
  activeDrawingSheetPositionPrefix,
  reloadLocalizedChrome,
  onPlanRefreshed,
  notifyPlanRefreshed
});

export { AppRuntime };
